import { IoLockClosedOutline } from "react-icons/io5";
import { BsShield, BsDatabaseCheck } from "react-icons/bs";
import { BiUniversalAccess } from "react-icons/bi";
import { getAccessToken } from "../../storage/storage";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";

export default function LodgifyConnection() {
  const token = getAccessToken();
  const { case: caseParam } = useParams();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const Navigate = useNavigate();

  const [apiKey, setApiKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState("");
  const [statusBanner, setStatusBanner] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // On mount: check if a key is already configured — if connected, skip the form entirely
  useEffect(() => {
    const checkAndAutoConnect = async () => {
      try {
        const statusRes = await axios.get(`${BASE_URL}/api/lodgify/connection-status/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { connection_status } = statusRes.data;

        if (connection_status === "connected") {
          // Key already stored — fetch properties directly and skip to selection
          const propsRes = await axios.get(`${BASE_URL}/api/properties/lodgify-properties/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          navigateToImport(propsRes.data);
          return;
        }

        if (connection_status === "expired") {
          setStatusBanner({
            type: "expired",
            message: "Your Lodgify API key has expired. Please enter a new one to reconnect.",
          });
        }
      } catch {
        // If status check fails, fall through and show the form normally
      } finally {
        setIsChecking(false);
      }
    };

    checkAndAutoConnect();
  }, []);

  const navigateToImport = (data) => {
    Navigate(
      caseParam === "2" ? "/lodgify-import-hkproperty" : "/lodgify-import-property",
      { state: { lodgifyData: data } }
    );
  };

  const handleAuthorize = () => {
    if (!apiKey.trim()) {
      setError("Please enter an API key");
      return;
    }
    setError("");
    setShowConfirm(true);
  };

  const handleConfirmedAuthorize = async () => {
    setShowConfirm(false);
    setIsLoading(true);

    try {
      let response;
      if (caseParam === "2") {
        response = await axios.post(
          `${BASE_URL}/api/properties/property-list/`,
          { ApiKey: apiKey },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${BASE_URL}/api/add-api-key/`,
          { api_key: apiKey },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      navigateToImport(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to authorize. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Full-screen loading while auto-checking connection status
  if (isChecking) {
    return (
      <div className="fixed inset-0 bg-white flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#586989]" />
        <p className="text-sm text-gray-500">Checking Lodgify connection...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 my-10 bg-white shadow-lg p-6 rounded-lg">
      <h1 className="md:text-xl text-lg font-bold text-[#586989] mb-4">
        Connect HouseKeeper to Lodgify
      </h1>
      <p className="text-gray-600 mb-6 text-start text-sm">
        Authorize secure access to Lodgify account to enable automatic booking synchronization
      </p>

      {/* Expired key warning */}
      {statusBanner?.type === "expired" && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {statusBanner.message}
        </div>
      )}

      <div className="border border-[#586989] rounded-md p-2 flex gap-2">
        <span className="ml-0 text-black mt-1"><BsShield /></span>
        <div className="text-sm text-black text-opacity-50">
          <strong>Secure Authorization Process:</strong>
          <span>
            {" "}HouseKeeper uses OAuth 2.0, the industry standard for secure authorization.
            Your Lodgify login credentials are never shared with HouseKeeper. You can revoke access at any time from your Lodgify account settings.
          </span>
        </div>
      </div>

      <div className="bg-[#FFF5CE] p-4 rounded-lg shadow">
        <h1 className="md:text-xl text-lg font-bold text-[#586989] mb-4">
          How We Handle Your Data
        </h1>
        <div className="mb-2">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <IoLockClosedOutline />Secure & Encryption
          </h2>
          <p className="text-sm">All data is transmitted using TLS 1.3 encryption and stored securely in compliance with industry standards.</p>
        </div>
        <div className="mb-2">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <BsShield />Privacy Compliance
          </h2>
          <p className="text-sm">Guest data is handled in full compliance with GDPR and other privacy regulations.</p>
        </div>
        <div className="mb-2">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <BsDatabaseCheck />Data Usage
          </h2>
          <p className="text-sm">Your data is used solely to provide cleaning management services and improve platform functionality.</p>
        </div>
        <div className="mb-2">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <BiUniversalAccess />Access Control
          </h2>
          <p className="text-sm">You can revoke HouseKeeper's access to your Lodgify account at any time from your Lodgify settings.</p>
        </div>
      </div>

      <div className="flex space-x-2 mt-2">
        <input
          type="password"
          placeholder="Enter API Key"
          className="p-2 rounded-md w-full border border-[#586989]"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAuthorize()}
        />
        <button
          onClick={handleAuthorize}
          disabled={isLoading}
          className="flex gap-2 bg-[#586989] text-white px-6 py-2 rounded-md disabled:opacity-50"
        >
          {isLoading ? "Authorizing..." : "Authorize"}
        </button>
      </div>

      {error && <p className="text-red-500 mt-2">{error}</p>}

      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <p className="text-lg font-semibold">Connecting to Lodgify...</p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mt-4" />
          </div>
        </div>
      )}

      {/* Step 2 — Confirm connection modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#FFF5CE] rounded-lg">
                <BsShield className="text-xl text-[#586989]" />
              </div>
              <h2 className="text-base font-bold text-[#586989]">Confirm Connection</h2>
            </div>
            <p className="text-sm text-gray-600">
              You're about to authorize HouseKeeper to connect to your Lodgify account using the provided API key. This will allow syncing of properties and bookings.
            </p>
            <ul className="text-sm text-gray-500 space-y-1 list-disc list-inside">
              <li>Your API key will be stored securely</li>
              <li>Properties and bookings will be synced</li>
              <li>You can revoke access at any time</li>
            </ul>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmedAuthorize}
                className="flex-1 px-4 py-2 bg-[#586989] text-white rounded-lg text-sm font-medium hover:bg-[#47546d] transition-colors"
              >
                Yes, Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
